import { useContext, useEffect, useState } from 'react';
import {
    Input,
    Button,
    Select,
    Row,
    Col,
    Tag,
    FormInstance,
    DatePicker,
    Radio,
    RadioChangeEvent,
    Checkbox,
} from 'antd';
import { useDispatch } from 'react-redux';
import { SearchOutlined } from '@ant-design/icons';
import { getCurrentTerm, getDefaultLayout } from 'constants/GlobalConfig';
import jaJP from 'antd/lib/date-picker/locale/ja_JP';
import isTouchDevice from 'is-touch-device';
import moment from 'moment';

import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';
import { userManagementTabFlex } from 'flex/view/UsersManagement/UserManagementTab';
import BaseForm from 'components/modules/BaseForm';

import { ListContext } from '..';
import TermSelect from 'components/modules/TermSelect';

const Form = BaseForm;
const { Option } = Select;

const STATUS_KEYS: { [key: string]: User.Status } = Object.freeze({
    active: 'active',
    deactive: 'deactive',
    plannedToDelete: 'planned_to_delete',
});

// 多要素認証の失敗回数は 1〜5（backend の MAX_AUTH_FAILURE_COUNT）を取り、
// 認証成功で 0 にリセットされるため、1 以上 =「現在認証に失敗しているユーザー」
const CURRENTLY_FAILING_MFA_COUNTS = [1, 2, 3, 4, 5];

const layout = getDefaultLayout();

type Props = {
    form: FormInstance<User.Search>;
};

const MfaSearch: React.FC<Props> = ({ form }) => {
    const [list] = useContext(ListContext);
    const dispatch = useDispatch();
    const termListSelector = useAppSelector(
        (state) => state.globalState.termList,
    );
    const { optionalLoading, organizationSelectLoading, termSelectLoading } =
        useAppSelector(userManagementTabFlex.selectors.loading);
    const {
        classSelectDisabled,
        roleSelectDisabled,
        statusSelectDisabled,
        organizationSelectDisabled,
        termSelectDisabled,
    } = useAppSelector(userManagementTabFlex.selectors.disabled);
    const usersSearchConnection = useAppSelector(
        (state) => state.usersSearchConnection,
    );
    const tabSelector = useAppSelector((state) => state.userManagementTab);
    const isControllableUser = useAppSelector(
        (state) => state.isControllableUser.payload,
    );
    const [isFullSearch, setIsFullSearch] = useState(false);
    const [durationCreatedStart, setDurationCreatedStart] =
        useState<moment.Moment | null>(null);
    const [durationCreatedEnd, setDurationCreatedEnd] =
        useState<moment.Moment | null>(null);
    const [durationUpdatedStart, setDurationUpdatedStart] =
        useState<moment.Moment | null>(null);
    const [durationUpdatedEnd, setDurationUpdatedEnd] =
        useState<moment.Moment | null>(null);
    const [durationCreatedStartMFA, setDurationCreatedStartMFA] =
        useState<moment.Moment | null>(null);
    const [durationCreatedEndMFA, setDurationCreatedEndMFA] =
        useState<moment.Moment | null>(null);

    useEffect(() => {
        const currentTermUuid = getCurrentTerm(termListSelector).uuid;
        form.setFieldsValue({
            term_uuid: currentTermUuid,
            role_uuid: undefined,
            school_class_uuid: undefined,
        });
        // termListSelector のみ監視したいため
    }, [termListSelector]); // eslint-disable-line react-hooks/exhaustive-deps

    function onFinish(values: Partial<User.SearchFormMFA>) {
        const params: any & {
            is_active: 0 | 1 | undefined;
            is_planned_to_delete: 0 | 1 | undefined;
        } = isFullSearch ? {
            ...values,
            created_start: values.created_start
                ? moment(values.created_start).unix()
                : null,
            created_end: values.created_end
                ? moment(values.created_end).unix()
                : null,
            updated_start: values.updated_start
                ? moment(values.updated_start).unix()
                : null,
            updated_end: values.updated_end
                ? moment(values.updated_end).unix()
                : null,
            created_start_mfa: values.created_start_mfa
                ? moment(values.created_start_mfa).unix()
                : null,
            created_end_mfa: values.created_end_mfa
                ? moment(values.created_end_mfa).unix()
                : null,
            failure_counts_mfa: values.failure_count_mfa
                ? CURRENTLY_FAILING_MFA_COUNTS
                : null,
            is_registered_mfa: values.is_registered_mfa?.length === 2 ? undefined : values.is_registered_mfa?.[0],
            is_active: undefined,
            is_planned_to_delete: undefined,
        } : {
            ...values,
            is_registered_mfa: values.is_registered_mfa?.length === 2 ? undefined : values.is_registered_mfa?.[0],
        };

        switch (params.status) {
            case STATUS_KEYS.active:
                params.is_active = 1;
                params.is_planned_to_delete = 0;
                break;
            case STATUS_KEYS.deactive:
                params.is_active = 0;
                params.is_planned_to_delete = 0;
                break;
            case STATUS_KEYS.plannedToDelete:
                params.is_active = undefined;
                params.is_planned_to_delete = 1;
                break;
            default:
                break;
        }

        const { status, ...data } = params;
        const current = tabSelector.pagination.current;
        dispatch(Actions.view.viewLogic.userManagementTab.search(data, current ,'mfa'));
    }

    function onReset() {
        form.resetFields();
        form.setFieldsValue({
            term_uuid: getCurrentTerm(termListSelector).uuid,
        });
        // DatePicker は value / disabledDate をローカル state で制御しているため、
        // form.resetFields() だけでは選択可能範囲が前回値のまま残る。明示的に初期化する。
        setDurationCreatedStart(null);
        setDurationCreatedEnd(null);
        setDurationUpdatedStart(null);
        setDurationUpdatedEnd(null);
        setDurationCreatedStartMFA(null);
        setDurationCreatedEndMFA(null);
        dispatch(Actions.view.viewLogic.userManagementTab.reset());
    }

    function handleSelectTerm(term_uuid: string) {
        form.resetFields(['school_class_uuid', 'role_uuid']);
        dispatch(
            Actions.view.viewLogic.userManagementTab.selectTerm(term_uuid),
        );
    }

    function handleSelectOrganization(uuid?: string) {
        form.resetFields(['school_class_uuid', 'role_uuid']);
        dispatch(
            Actions.view.viewLogic.userManagementTab.selectOrganization(uuid),
        );
    }

    function disabledCreatedStartDate(currentDate: moment.Moment) {
        if (!durationCreatedEnd) return false;
        return currentDate.valueOf() >= durationCreatedEnd.valueOf();
    }

    function disabledCreatedEndDate(currentDate: moment.Moment) {
        if (!durationCreatedStart) return false;
        return currentDate.valueOf() <= durationCreatedStart.valueOf();
    }

    function disabledUpdatedStartDate(currentDate: moment.Moment) {
        if (!durationUpdatedEnd) return false;
        return currentDate.valueOf() >= durationUpdatedEnd.valueOf();
    }

    function disabledUpdatedEndDate(currentDate: moment.Moment) {
        if (!durationUpdatedStart) return false;
        return currentDate.valueOf() <= durationUpdatedStart.valueOf();
    }

    function disabledCreatedStartMFADate(currentDate: moment.Moment) {
        if (!durationCreatedEndMFA) return false;
        return currentDate.valueOf() >= durationCreatedEndMFA.valueOf();
    }

    function disabledCreatedEndMFADate(currentDate: moment.Moment) {
        if (!durationCreatedStartMFA) return false;
        return currentDate.valueOf() <= durationCreatedStartMFA.valueOf();
    }

    function onModeChange(e: RadioChangeEvent) {
        setIsFullSearch(e.target.value === 'full');
    }

    return (
        <div className='card general-search notification-search-form-wrapper'>
            {/* 検索モード切り替え */}
            <div className='mb-8'>
                <Radio.Group
                    onChange={onModeChange}
                    defaultValue='simple'
                    buttonStyle='solid'
                >
                    <Radio.Button value='simple'>シンプルな検索</Radio.Button>
                    <Radio.Button value='full'>詳細検索</Radio.Button>
                </Radio.Group>
            </div>

            <Form<User.Search>
                {...layout}
                form={form}
                name='control-hooks-users-search'
                onFinish={onFinish}
            >
                {/* シンプルな検索: 学校, クラス, 役割, 多要素認証 */}
                {isControllableUser && (
                    <Form.Item
                        name='organization_uuid'
                        label='学校'
                    >
                        <Select<string>
                            placeholder='学校を選択してください'
                            onChange={handleSelectOrganization}
                            onClear={handleSelectOrganization}
                            disabled={organizationSelectDisabled}
                            loading={organizationSelectLoading}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                (option!.children as unknown as string)
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        >
                            {list.organizationList.map((value, index) => {
                                return (
                                    <Option
                                        key={index}
                                        value={value.uuid}
                                    >
                                        {value.name}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item
                    name='school_class_uuid'
                    label='クラス'
                >
                    <Row gutter={[8, 0]}>
                        <Col span={12}>
                            <Form.Item
                                name='term_uuid'
                                style={{ marginBottom: '0px' }}
                            >
                                <TermSelect
                                    value={tabSelector.selectTerm}
                                    onChange={handleSelectTerm}
                                    disabled={termSelectDisabled}
                                    loading={termSelectLoading}
                                    termList={termListSelector}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name='school_class_uuid'
                                style={{ marginBottom: '0px' }}
                            >
                                <Select
                                    disabled={classSelectDisabled}
                                    placeholder='クラスを選択してください'
                                    loading={optionalLoading}
                                    allowClear
                                >
                                    {(
                                        list.classListMap[
                                            tabSelector.selectTerm ?? ''
                                        ] ?? []
                                    ).map((value, index) => {
                                        return (
                                            <Option
                                                key={index}
                                                value={value.uuid}
                                            >
                                                <Tag
                                                    color='#AAAAAA'
                                                    style={{
                                                        width: '90px',
                                                        textAlign: 'center',
                                                        borderRadius: '5px',
                                                    }}
                                                >
                                                    {value.term.name}
                                                </Tag>
                                                {value.name}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form.Item>

                <Form.Item
                    name='role_uuid'
                    label='役割'
                >
                    <Select
                        disabled={roleSelectDisabled}
                        placeholder='役割を選択してください'
                        loading={optionalLoading}
                        allowClear
                    >
                        {list.roleList.map((value, index) => {
                            return (
                                <Option
                                    key={index}
                                    value={value.uuid}
                                >
                                    {value.name}
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>

                {/* 詳細検索: 状態, ユーザーID, 名前, 作成時間, 更新時間 */}
                {isFullSearch && (
                    <>
                        <Form.Item
                            name='status'
                            label='状態'
                        >
                            <Select
                                disabled={statusSelectDisabled}
                                placeholder='状態を選択してください'
                                loading={optionalLoading}
                                allowClear
                            >
                                <Option value={STATUS_KEYS.active}>有効</Option>
                                <Option value={STATUS_KEYS.deactive}>
                                    無効
                                </Option>
                                <Option value={STATUS_KEYS.plannedToDelete}>
                                    削除予定
                                </Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name='login_id'
                            label='ユーザーID'
                        >
                            <Input placeholder='ユーザーIDを入力してください' />
                        </Form.Item>

                        <Form.Item label='名前'>
                            <Row gutter={[8, 0]}>
                                <Col span={24}>
                                    <Form.Item name='last_name'>
                                        <Input placeholder='姓を入力してください' />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={[8, 0]}>
                                <Col span={24}>
                                    <Form.Item name='first_name'>
                                        <Input placeholder='名を入力してください' />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form.Item>

                        <Form.Item
                            label='作成時間'
                            extra='指定された期間内に新規作成されたユーザーに適用されます。'
                        >
                            <Input.Group>
                                <Row gutter={8}>
                                    <Col span={12}>
                                        <Form.Item
                                            noStyle
                                            name='created_start'
                                        >
                                            <DatePicker
                                                locale={jaJP}
                                                showTime={{ format: 'HH:mm' }}
                                                format='YYYY/MM/DD HH:mm'
                                                disabledDate={
                                                    disabledCreatedStartDate
                                                }
                                                value={durationCreatedStart}
                                                placeholder='開始日時'
                                                onChange={
                                                    setDurationCreatedStart
                                                }
                                                style={{ width: '100%' }}
                                                inputReadOnly={isTouchDevice()}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            noStyle
                                            name='created_end'
                                        >
                                            <DatePicker
                                                locale={jaJP}
                                                showTime={{ format: 'HH:mm' }}
                                                format='YYYY/MM/DD HH:mm'
                                                disabledDate={
                                                    disabledCreatedEndDate
                                                }
                                                value={durationCreatedEnd}
                                                placeholder='終了日時'
                                                onChange={setDurationCreatedEnd}
                                                style={{ width: '100%' }}
                                                inputReadOnly={isTouchDevice()}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Input.Group>
                        </Form.Item>

                        <Form.Item
                            label='更新時間'
                            extra='指定された期間内に更新されたユーザーに適用されます。'
                        >
                            <Input.Group>
                                <Row gutter={8}>
                                    <Col span={12}>
                                        <Form.Item
                                            noStyle
                                            name='updated_start'
                                        >
                                            <DatePicker
                                                locale={jaJP}
                                                showTime={{ format: 'HH:mm' }}
                                                format='YYYY/MM/DD HH:mm'
                                                disabledDate={
                                                    disabledUpdatedStartDate
                                                }
                                                value={durationUpdatedStart}
                                                placeholder='開始日時'
                                                onChange={
                                                    setDurationUpdatedStart
                                                }
                                                style={{ width: '100%' }}
                                                inputReadOnly={isTouchDevice()}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            noStyle
                                            name='updated_end'
                                        >
                                            <DatePicker
                                                locale={jaJP}
                                                showTime={{ format: 'HH:mm' }}
                                                format='YYYY/MM/DD HH:mm'
                                                disabledDate={
                                                    disabledUpdatedEndDate
                                                }
                                                value={durationUpdatedEnd}
                                                placeholder='終了日時'
                                                onChange={setDurationUpdatedEnd}
                                                style={{ width: '100%' }}
                                                inputReadOnly={isTouchDevice()}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Input.Group>
                        </Form.Item>

                        <Form.Item
                            name='failure_count_mfa'
                            label='多要素認証失敗状況'
                            valuePropName='checked'
                        >
                            <Checkbox>
                                現在認証に失敗しているユーザー
                            </Checkbox>
                        </Form.Item>

                        <Form.Item
                            label='多要素認証登録日時（開始）'
                            name='created_start_mfa'
                        >
                            <DatePicker
                                locale={jaJP}
                                showTime={{ format: 'HH:mm' }}
                                format='YYYY/MM/DD HH:mm'
                                disabledDate={disabledCreatedStartMFADate}
                                value={durationCreatedStartMFA}
                                placeholder='開始日時'
                                onChange={setDurationCreatedStartMFA}
                                style={{ width: '100%' }}
                                inputReadOnly={isTouchDevice()}
                            />
                        </Form.Item>
                        <Form.Item
                            label='多要素認証登録日時（終了）'
                            name='created_end_mfa'
                        >
                            <DatePicker
                                locale={jaJP}
                                showTime={{ format: 'HH:mm' }}
                                format='YYYY/MM/DD HH:mm'
                                disabledDate={disabledCreatedEndMFADate}
                                value={durationCreatedEndMFA}
                                placeholder='終了日時'
                                onChange={setDurationCreatedEndMFA}
                                style={{ width: '100%' }}
                                inputReadOnly={isTouchDevice()}
                            />
                        </Form.Item>
                    </>
                )}

                {/* 多要素認証 設定済/未設定フィルター */}
                <Form.Item
                    name='is_registered_mfa'
                    label='多要素認証'
                >
                    <Checkbox.Group>
                        <Checkbox value={1}>設定済</Checkbox>
                        <Checkbox value={0}>未設定</Checkbox>
                    </Checkbox.Group>
                </Form.Item>

                <div className='submit-container'>
                    <Button
                        htmlType='button'
                        onClick={onReset}
                    >
                        リセット
                    </Button>
                    <Button
                        type='primary'
                        htmlType='submit'
                        loading={usersSearchConnection.meta.fetch}
                        icon={<SearchOutlined />}
                    >
                        検索
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default MfaSearch;
